<template>
  <FeeScheduleListPanel @close="$event => $emit('close', $event)">
    <FeeScheduleItemLayout
      :section-classes="[$style['fee-schedule-item']]"
      :header-classes="[$style['title-row']]"
      :title-classes="[$style['title-left']]"
      :action-ct-classes="[$style['action-buttons-container']]"
    >
      <template #title>
        <BaseTitle :schedule="schedule" :metadata-items="visibleTitleMetadataList">
          {{ name }}
        </BaseTitle>
      </template>
      <FeeScheduleDetailsSection v-model="showDetailSection" :metadata-items="metadataList">
        <data-table :columns="headers" :rows="rows">
          <template #timeSchedule="{ row }">
            {{ getHistoricFeeStartDate(row.start) }} - {{ getFormattedDate(row.end) }}
          </template>
          <template #totalAnnualFee="{ row }">
            {{ formatNumber(row.totalAnnualFee, 'shortDecimal') }}
          </template>
          <template #totalAnnualMaxFee="{ row }">
            {{ formatNumber(row.totalAnnualMaxFee, 'shortDecimal') }}
          </template>
        </data-table>
      </FeeScheduleDetailsSection>
    </FeeScheduleItemLayout>
  </FeeScheduleListPanel>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import FeeScheduleListPanel from './FeeScheduleListPanel';
import FeeScheduleItemLayout from '../../common/layout/FeeScheduleItemLayout';
import BaseTitle from '../../common/title/BaseTitle';
import FeeScheduleDetailsSection from '../../common/FeeScheduleDetailsSection';
import DataTable from '../../common/DataTable';
import labels from './labels.json';
import { getHistoricMaxOrManagerFeeStartDate } from '../../utils';

export default {
  name: 'annual-fee-total-display',
  components: {
    FeeScheduleListPanel,
    FeeScheduleItemLayout,
    BaseTitle,
    FeeScheduleDetailsSection,
    DataTable,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    name: String,
    schedule: {
      type: Object,
    },
    isAnnualMaxFeeTotal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      defaultConfig: {
        settings: {
          languageId: 'en-US',
          enableThreeReturns: false,
        },
        labels,
      },
      showDetailSection: true,
    };
  },
  provide() {
    return {
      translate: this.translate,
      formatNumber: this.formatNumber,
    };
  },
  computed: {
    titleMetadataList() {
      return [
        {
          label: this.translate('historicFee'),
        },
      ];
    },
    metadataList() {
      return this.titleMetadataList.map(metadataItem => metadataItem.label);
    },
    visibleTitleMetadataList() {
      if (this.showDetailSection) {
        return [];
      }
      return this.titleMetadataList;
    },
    headers() {
      const headers = [
        {
          text: this.translate('timeSchedule'),
          fieldName: 'timeSchedule',
          align: 'left',
          style: {
            width: '120px',
          },
        },
      ];
      if (this.isAnnualMaxFeeTotal) {
        headers.push(this.annualMaxFeeTotalColumn);
      } else {
        if (this.settings.enableThreeReturns) {
          headers.push(this.annualMaxFeeTotalColumn);
        }
        headers.push(this.annualTotalFeeColumn);
      }
      return headers;
    },
    annualMaxFeeTotalColumn() {
      return {
        text: this.translate('totalAnnualMaxFeePercent'),
        fieldName: 'totalAnnualMaxFee',
        align: 'right',
        style: {
          width: '80px',
        },
      };
    },
    annualTotalFeeColumn() {
      return {
        text: this.settings.enableThreeReturns
          ? this.translate('totalAnnualProposedFeePercent')
          : this.translate('totalAnnualFeePercent'),
        fieldName: 'totalAnnualFee',
        align: 'right',
        style: {
          width: '80px',
        },
      };
    },
    rows() {
      return this.schedule.feeScheduleDetails;
    },
  },
  methods: {
    getFormattedDate(dateStr) {
      const date = new Date(dateStr);
      const monthStr = date.toLocaleString('default', { month: 'short' });
      const dayStr = String(date.getDate()).padStart(2, '0');
      const yearStr = String(date.getFullYear()).padStart(4, '0');
      return `${monthStr} ${dayStr}, ${yearStr}`;
    },
    getHistoricFeeStartDate(dateStr) {
      return getHistoricMaxOrManagerFeeStartDate(
        dateStr,
        this.getFormattedDate,
        this.translate,
        this.settings.isActualWorkflow
      );
    },
  },
};
</script>

<style lang="scss" module>
@import '../../styles/fee-schedule-item.scss';
</style>
